import React, { useState, useEffect } from "react";
import DepartmentBanner from "../common/DepartmentBanner";
import TermSelector from "../common/TermSelector";
import OfficeHourService from "../../services/Department/OfficeHourService";
import DepartmentService from "../../services/Department/DepartmentService";
import styles from './FacultyOfficeHours.module.css';
import { FaSearch, FaSpinner, FaTable, FaTh, FaSort, FaSortUp, FaSortDown, FaUserTie, FaClock, FaMapMarkerAlt, FaEnvelope, FaPhone, FaCalendarAlt } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import Pagination from '../common/Pagination';

const FacultyOfficeHours = () => {
  document.title = "Faculty Office Hours";
  const params = new URLSearchParams(window.location.search);


  const [selectedTerm, setSelectedTerm] = useState(0);
  const [selectedTermDetails, setSelectedTermDetails] = useState({});
  const [officeHours, setOfficeHours] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState('table');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [disabledColumns, setDisabledColumns] = useState([]);
  const itemsPerPage = 20;

  // Define column types
  const COLUMN_TYPES = Object.freeze({
    INSTRUCTOR: 22,
    OFFICE_DAY: 23,
    OFFICE_HOUR: 24,
    LOCATION: 25,
    PHONE: 26,
    EMAIL: 27
  });

  const SORT_KEYS = Object.freeze({
    INSTRUCTOR: 'Instructor.fullName',
    EMAIL: 'Instructor.email',
    PHONE: 'Instructor.officePhone'
  });

  // Check if a column is enabled
  const isColumnEnabled = (columnType) => {
    return !disabledColumns.includes(columnType);
  };

  const handleTermChangeEvent = (termId, selectedTerm) => {
    setSelectedTerm(termId);
    setSelectedTermDetails(selectedTerm);
  };

  useEffect(() => {
    if (selectedTerm) {
      fetchOfficeHours(selectedTerm);
    }
  }, [selectedTerm]);

  useEffect(() => {
    // Get column configuration
    DepartmentService.GetColumnConfig(5)
      .then(response => {
        const { serviceDisabledItems } = response.data || response;
        if (serviceDisabledItems && serviceDisabledItems.trim() !== "") {
          setDisabledColumns(serviceDisabledItems.split(',').map(item => parseInt(item.trim())));
        } else {
          setDisabledColumns([]);
        }
      })
      .catch(error => {
        console.error('Error fetching column configuration:', error);
        setDisabledColumns([]);
      });
  }
    , []);

  const fetchOfficeHours = (selectedTerm) => {
    setIsLoading(true);
    OfficeHourService.GetByDepartmentAndTerm(selectedTerm)
      .then(response => {
        setOfficeHours(response.data);
        setIsLoading(false);
      })
      .catch(error => {
        setIsLoading(false);
        console.error('Error fetching page content:', error);
      });
  };

  const filteredOfficeHours = officeHours.filter(item =>
    item?.Instructor?.fullName.toLowerCase().includes(searchTerm?.toLowerCase()) ||
    item?.Instructor?.email.toLowerCase().includes(searchTerm?.toLowerCase()) ||
    item?.officeHours.some(hour =>
      hour?.dayofweek.toLowerCase().includes(searchTerm?.toLowerCase()) ||
      hour?.discription.toLowerCase().includes(searchTerm?.toLowerCase())
    )
  );

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortedOfficeHours = (hours) => {
    if (!sortConfig.key) return hours;

    return [...hours].sort((a, b) => {
      let aValue = sortConfig.key.split('.').reduce((obj, key) => obj[key], a)?.toLowerCase() || '';
      let bValue = sortConfig.key.split('.').reduce((obj, key) => obj[key], b)?.toLowerCase() || '';

      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const getSortIcon = (columnName) => {
    if (sortConfig.key !== columnName) {
      return <FaSort className="ms-1 text-muted" />;
    }
    return sortConfig.direction === 'asc'
      ? <FaSortUp className="ms-1" />
      : <FaSortDown className="ms-1" />;
  };

  // Calculate pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = getSortedOfficeHours(filteredOfficeHours).slice(indexOfFirstItem, indexOfLastItem);
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo(0, 0);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="container-fluid p-0"
      style={{ backgroundColor: '#f8f9fa' }}
    >
      <div style={{ position: 'relative', zIndex: 1 }}>
        {/* <DepartmentBanner /> */}

        <div className="container-custom py-4">
          {/* Breadcrumb */}
          <nav aria-label="breadcrumb" className="mb-4">
            <ol className="breadcrumb bg-transparent p-0">
              <li className="breadcrumb-item">

              </li>
              {/* <li className="breadcrumb-item active" aria-current="page">Faculty Office Hours</li> */}
            </ol>
          </nav>

          {/* Term Selector and Stats */}
          <div className="row mb-4">
            <div className="col-md-4">
              <div className="card border-0 shadow-sm stats-card">
                <div className="card-body">
                  <TermSelector onTermChangeEvent={handleTermChangeEvent} />
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card border-0 shadow-sm stats-card">
                <div className="card-body">
                  <p className="text-muted mb-2 h3">Total Faculty</p>
                  <p className="mb-0 h3">{officeHours.length}</p>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card border-0 shadow-sm stats-card">
                <div className="card-body">
                  <p className="text-muted mb-2">Current Term</p>
                  <p className="mb-0 h3">{selectedTermDetails.termWithYearLast || 'Not Selected'}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Header Section */}
          <div className="card shadow-sm border-0 mb-4">
            <div className="card-body">
              <div className="row align-items-center">
                <div className="col-lg-3">
                  <p className="mb-0 h3">Office Hours</p>
                  <p className="text-muted mb-0">Faculty availability schedule</p>
                </div>
                <div className="col-lg-9">
                  <div className="d-flex gap-3 flex-wrap">
                    <div className="flex-grow-1">
                      <div className="input-group">
                        <span className="input-group-text bg-white border-end-0">
                          <FaSearch className="text-muted" />
                        </span>
                        <input
                          type="text"
                          className="form-control border-start-0"
                          placeholder="Search office hours..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="btn-group view-toggle">
                      <button type="button" aria-label="Open Table"
                        className={`btn ${viewMode === 'table' ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => setViewMode('table')}
                      >
                        <FaTable />
                      </button>
                      <button type="button" aria-label="Open Grid"
                        className={`btn ${viewMode === 'grid' ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => setViewMode('grid')}
                      >
                        <FaTh />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Content Section */}
          <AnimatePresence mode="wait">
            {isLoading ? (
              <div className="card shadow-sm border-0">
                <div className="card-body">
                  <div className="text-center py-5">
                    <p className="text-muted mb-0">Loading office hours...</p>
                  </div>
                </div>
              </div>
            ) : viewMode === 'table' ? (
              <motion.div
                key="table"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="card shadow-sm border-0"
              >
                <div className="card-body p-0">
                  <div className={styles.tableWrapper}>
                    <table className="table table-hover mb-0">
                      <thead>
                        <tr className="bg-light">
                          {isColumnEnabled(COLUMN_TYPES.INSTRUCTOR) && (
                            <th
                              className={`${styles.tableCell} ${styles.instructorCell} cursor-pointer`}
                              onClick={() => requestSort(SORT_KEYS.INSTRUCTOR)}
                            >
                              Instructor {getSortIcon(SORT_KEYS.INSTRUCTOR)}
                            </th>
                          )}
                          {isColumnEnabled(COLUMN_TYPES.OFFICE_DAY) && (
                            <th className={`${styles.tableCell} ${styles.dayCell}`}>Office Day</th>
                          )}
                          {isColumnEnabled(COLUMN_TYPES.OFFICE_HOUR) && (
                            <th className={`${styles.tableCell} ${styles.hourCell}`}>Office Hour</th>
                          )}
                          <th className={`${styles.tableCell} ${styles.hourCell}`}>Note</th>
                          {isColumnEnabled(COLUMN_TYPES.LOCATION) && (
                            <th className={`${styles.tableCell} ${styles.locationCell}`}>Location</th>
                          )}
                          {isColumnEnabled(COLUMN_TYPES.PHONE) && (
                            <th className={`${styles.tableCell} ${styles.phoneCell}`}>Phone</th>
                          )}
                          {isColumnEnabled(COLUMN_TYPES.EMAIL) && (
                            <th className={`${styles.tableCell} ${styles.emailCell}`}>Email</th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {currentItems.length > 0 ? (
                          currentItems.map((faculty, index) => (
                            faculty.Instructor ? ( // <-- check if Instructor exists
                              faculty.officeHours.map((hour, hourIndex) => (
                                <motion.tr
                                  key={`${index}-${hourIndex}`}
                                  initial={{ opacity: 0 }}
                                  animate={{ opacity: 1 }}
                                  transition={{ delay: index * 0.05 }}
                                  className="align-middle"
                                >
                                  {hourIndex === 0 && isColumnEnabled(COLUMN_TYPES.INSTRUCTOR) && (
                                    <td
                                      rowSpan={faculty.officeHours.length}
                                      className={`${styles.tableCell} ${styles.instructorCell}`}
                                    >
                                      <div className="d-flex align-items-center">
                                        <div className="rounded-circle bg-primary bg-opacity-10 p-2 me-2">
                                          <FaUserTie className="text-primary" />
                                        </div>
                                        <span>{faculty.Instructor.fullName}</span>
                                      </div>
                                    </td>
                                  )}
                                  {isColumnEnabled(COLUMN_TYPES.OFFICE_DAY) && (
                                    <td className={`${styles.tableCell} ${styles.dayCell}`}>
                                      <FaCalendarAlt className="me-2 text-muted" />
                                      {hour.dayofweek}
                                    </td>
                                  )}
                                  {isColumnEnabled(COLUMN_TYPES.OFFICE_HOUR) && (
                                    <td className={`${styles.tableCell} ${styles.hourCell}`}>
                                      <FaClock className="me-2 text-muted" />
                                      {hour.time ? hour.time : hour.discription}
                                    </td>
                                  )}
                                  <td className={`${styles.tableCell} ${styles.hourCell}`}>
                                    {hour.discription}
                                  </td>

                                  {hourIndex === 0 && isColumnEnabled(COLUMN_TYPES.LOCATION) && (
                                    <td
                                      rowSpan={faculty.officeHours.length}
                                      className={`${styles.tableCell} ${styles.locationCell}`}
                                    >
                                      <FaMapMarkerAlt className="me-2 text-muted" />
                                      {faculty.Instructor.officeAddress}
                                    </td>
                                  )}
                                  {hourIndex === 0 && isColumnEnabled(COLUMN_TYPES.PHONE) && (
                                    <td
                                      rowSpan={faculty.officeHours.length}
                                      className={`${styles.tableCell} ${styles.phoneCell}`}
                                    >
                                      <a href={`tel:${faculty.Instructor.officePhone}`} className={styles.link}>
                                        <FaPhone className="me-2" />
                                        {faculty.Instructor.officePhone}
                                      </a>
                                    </td>
                                  )}
                                  {hourIndex === 0 && isColumnEnabled(COLUMN_TYPES.EMAIL) && (
                                    <td
                                      rowSpan={faculty.officeHours.length}
                                      className={`${styles.tableCell} ${styles.emailCell}`}
                                    >
                                      <a href={`mailto:${faculty.Instructor.email}`} className={styles.link}>
                                        <FaEnvelope className="me-2" />
                                        {faculty.Instructor.email}
                                      </a>
                                    </td>
                                  )}
                                </motion.tr>
                              ))
                            ) : '' // <-- skip if Instructor is null
                          ))
                        ) : (
                          <tr>
                            <td colSpan={Object.values(COLUMN_TYPES).filter(type => isColumnEnabled(type)).length + 1} className="text-center py-5">
                              <p className="text-muted mb-0">No office hours found</p>
                            </td>
                          </tr>
                        )}

                      </tbody>
                    </table>
                  </div>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="grid"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="row g-4"
              >
                {currentItems.map((faculty, index) => (
                  <motion.div
                    key={index}
                    className="col-md-6 col-lg-4"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <div className="card h-100 border-0 shadow-sm hover-shadow">
                      <div className="card-body">
                        {isColumnEnabled(COLUMN_TYPES.INSTRUCTOR) && (
                          <div className="d-flex align-items-center mb-4">
                            <div className="rounded-circle bg-primary bg-opacity-10 p-3 me-3">
                              <FaUserTie className="text-primary" size={24} />
                            </div>
                            <div>
                              <h5 className="card-title mb-1">{faculty.Instructor.fullName}</h5>
                              <div className="d-flex align-items-center gap-2">
                                {isColumnEnabled(COLUMN_TYPES.EMAIL) && (
                                  <>
                                    <a href={`mailto:${faculty.Instructor.email}`} className={styles.link}>
                                      <FaEnvelope className="me-1" />
                                      Email
                                    </a>
                                    {isColumnEnabled(COLUMN_TYPES.PHONE) && <span className="text-muted">|</span>}
                                  </>
                                )}
                                {isColumnEnabled(COLUMN_TYPES.PHONE) && (
                                  <a href={`tel:${faculty.Instructor.officePhone}`} className={styles.link}>
                                    <FaPhone className="me-1" />
                                    Call
                                  </a>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                        {isColumnEnabled(COLUMN_TYPES.LOCATION) && (
                          <div className="mb-3">
                            <div className="d-flex align-items-center mb-2">
                              <FaMapMarkerAlt className="text-primary me-2" />
                              <strong>Location:</strong>
                            </div>
                            <p className="ms-4 mb-3">{faculty.Instructor.officeAddress}</p>
                          </div>
                        )}
                        {(isColumnEnabled(COLUMN_TYPES.OFFICE_DAY) || isColumnEnabled(COLUMN_TYPES.OFFICE_HOUR)) && (
                          <div>
                            <div className="d-flex align-items-center mb-2">
                              <FaClock className="text-primary me-2" />
                              <strong>Office Hours:</strong>
                            </div>
                            {faculty.officeHours.map((hour, hourIndex) => (
                              <div key={hourIndex} className={styles.timeSlot}>
                                {isColumnEnabled(COLUMN_TYPES.OFFICE_DAY) && (
                                  <div className="fw-bold text-primary mb-1">{hour.dayofweek}</div>
                                )}
                                {isColumnEnabled(COLUMN_TYPES.OFFICE_HOUR) && (
                                  <div>{hour.discription}</div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
                {currentItems.length === 0 && (
                  <div className="col-12 text-center py-5">
                    <p className="text-muted mb-0">No office hours found</p>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Pagination */}
      <Pagination
        currentPage={currentPage}
        totalItems={getSortedOfficeHours(filteredOfficeHours).length}
        itemsPerPage={itemsPerPage}
        onPageChange={handlePageChange}
      />
    </motion.div>
  );
};

export default FacultyOfficeHours;